import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Clock, Inbox, Lock, Mail, MapPin, MessageCircle, Phone, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import { useModules } from '../../context/FeatureContext';
import { useAuth } from '../../context/AuthContext';
import {
  PAGE, PAGE_WIDE, DataTable, Dialog, EmptyState, StatusPill, Tabs, fmtDate, fmtDateTime, humanise, num, plural,
} from '../../components/ui';
import WebsiteChatSection from './WebsiteChatSection';

// Website chat: the switch and embed code (WebsiteChatSection) and the leads the chat widget has
// captured. Web-chat leads are receptionist leads with channel 'web_chat'; the same list is the
// "Web chat" channel in AI receptionist, and /chatbot sends organisations with that module there.

const BAND_LABEL = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };
function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const band = s >= 61 ? 'hot' : s >= 31 ? 'warm' : 'cold';
  return <StatusPill status={band} label={`${BAND_LABEL[band]} · ${s}`} />;
}

const toTime = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};
const transcriptOf = (lead) => {
  const t = lead?.transcript;
  if (Array.isArray(t)) return t;
  if (typeof t === 'string' && t.trim()) {
    try { const parsed = JSON.parse(t); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
};
const visitorName = (l) => l?.name || 'Website visitor';

/* ─── Lead detail (read-only) ────────────────────────────────────────────── */
function WebChatLeadDialog({ lead, onClose }) {
  const [tab, setTab] = useState('details');
  const transcript = transcriptOf(lead);
  const linkCls = 'text-primary-700 underline-offset-2 hover:underline';
  const details = [
    [Phone, 'Phone', lead.phone ? <a href={`tel:${lead.phone}`} className={linkCls}>{lead.phone}</a> : null],
    [Mail, 'Email', lead.email ? <a href={`mailto:${lead.email}`} className={clsx(linkCls, 'break-all')}>{lead.email}</a> : null],
    [MapPin, 'Suburb', lead.suburb],
    [Clock, 'Urgency', lead.urgency ? humanise(lead.urgency) : null],
  ].filter(([, , value]) => value);
  const summary = lead.ai_summary || lead.message || '';

  return (
    <Dialog open onClose={onClose} size="lg" title={visitorName(lead)}
      description={`Web chat · ${fmtDateTime(lead.created_at)}`}
      footer={<button type="button" className="btn-secondary" onClick={onClose}>Close</button>}>
      <div data-autofocus tabIndex={-1} className="mb-3 flex flex-wrap items-center gap-2 outline-none">
        <ScoreBadge score={lead.lead_score} />
        <StatusPill status={lead.status || 'new'} />
      </div>
      <Tabs value={tab} onChange={setTab} ariaLabel="Lead sections"
        tabs={[{ id: 'details', label: 'Details' }, { id: 'transcript', label: 'Transcript', count: transcript.length }]} />
      <div className="pt-4">
        {tab === 'details' && (
          <div className="space-y-4">
            {details.length > 0 && (
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {details.map(([Icon, label, value]) => (
                  <div key={label} className="flex min-w-0 items-start gap-2 text-[13px]">
                    <Icon size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                    <dt className="shrink-0 text-slate-500">{label}</dt>
                    <dd className="min-w-0 font-medium text-slate-800">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {lead.support_needs && (
              <div>
                <p className="text-[12.5px] font-semibold text-slate-700">Support needs</p>
                <p className="mt-1 text-[13px] leading-relaxed text-slate-700">{lead.support_needs}</p>
              </div>
            )}
            {summary && (
              <div>
                <p className="text-[12.5px] font-semibold text-slate-700">{lead.ai_summary ? 'AI summary' : 'Message'}</p>
                <p className="mt-1 rounded-lg bg-slate-50 p-3 text-[13px] leading-relaxed text-slate-700">{summary}</p>
              </div>
            )}
            {!details.length && !lead.support_needs && !summary && (
              <p className="text-[13px] text-slate-500">The visitor didn't leave any details. The transcript shows what they said.</p>
            )}
          </div>
        )}
        {tab === 'transcript' && (
          transcript.length === 0 ? (
            <EmptyState icon={MessageCircle} title="No transcript" hint="This chat didn't save a transcript." />
          ) : (
            <ol className="space-y-2.5">
              {transcript.map((m, i) => {
                const visitor = m?.role === 'user';
                return (
                  <li key={i} className={clsx('flex flex-col', visitor ? 'items-end' : 'items-start')}>
                    <span className="mb-0.5 text-[11.5px] font-medium text-slate-500">{visitor ? 'Visitor' : 'Receptionist'}</span>
                    <span className={clsx('max-w-[82%] whitespace-pre-wrap rounded-xl px-3 py-2 text-[13px] leading-relaxed',
                      visitor ? 'bg-slate-800 text-white' : 'border border-slate-200 bg-white text-slate-700')}>
                      {m?.content || ''}
                    </span>
                  </li>
                );
              })}
            </ol>
          )
        )}
      </div>
    </Dialog>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────── */
export default function ChatbotDashboard() {
  const modules = useModules();
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const allowed = isSuper || modules.ai_chatbot === true;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The leads endpoints filter by channel themselves, so this is every web-chat lead up to 500.
      const url = isSuper
        ? '/receptionist/leads?channel=web_chat&limit=500'
        : '/receptionist/my-leads?channel=web_chat&limit=500';
      const r = await api.get(url);
      const all = Array.isArray(r.data?.leads) ? r.data.leads : [];
      setLeads(all.filter((l) => l?.channel === 'web_chat'));
    } catch (e) {
      setLeads([]);
      setError(e?.response?.data?.message || "We couldn't load your web-chat leads. Check your connection, then try again.");
    } finally {
      setLoading(false);
    }
  }, [isSuper]);

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
  }, [allowed, load]);

  const refresh = () => { setReloadKey((k) => k + 1); load(); };
  const closeLead = useCallback(() => setSelected(null), []);

  const thisWeek = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return leads.filter((l) => (toTime(l.created_at) || 0) >= weekAgo).length;
  }, [leads]);

  /* ── Gate: an organisation without the module ── */
  if (!allowed) {
    return (
      <div className={PAGE}>
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState icon={Lock} title="Website chat isn't enabled"
            hint="Website chat isn't turned on for your organisation. Contact your administrator to request access." />
        </div>
      </div>
    );
  }

  const columns = [
    {
      key: 'name', header: 'Name', sortable: true, sortValue: (l) => (l.name ? l.name.toLowerCase() : null),
      render: (l) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); setSelected(l); }}
          className={clsx('text-left font-semibold underline-offset-2 hover:text-primary-700 hover:underline',
            l.name ? 'text-slate-900' : 'text-slate-600')}>
          {visitorName(l)}
        </button>
      ),
    },
    {
      key: 'lead_score', header: 'Score', sortable: true, defaultDir: 'desc', sortValue: (l) => Number(l.lead_score) || 0,
      render: (l) => <ScoreBadge score={l.lead_score} />,
    },
    {
      key: 'status', header: 'Status', sortable: true, sortValue: (l) => l.status || null,
      render: (l) => <StatusPill status={l.status || 'new'} />,
    },
    {
      key: 'summary', header: 'Message or summary',
      render: (l) => {
        const s = l.ai_summary || l.message || '';
        return s ? <span className="block max-w-[34rem] truncate text-slate-700" title={s}>{s}</span> : <span className="text-slate-500">—</span>;
      },
    },
    {
      key: 'created_at', header: 'Date', sortable: true, defaultDir: 'desc', sortValue: (l) => toTime(l.created_at),
      render: (l) => <span className="whitespace-nowrap tabular-nums text-slate-700" title={fmtDateTime(l.created_at)}>{fmtDate(l.created_at)}</span>,
    },
  ];

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] tabular-nums text-slate-600" aria-live="polite">
          {loading ? 'Loading web-chat leads…'
            : error ? 'Web-chat leads could not be loaded'
              : `${plural(leads.length, 'web-chat lead')} · ${num(thisWeek)} this week`}
        </p>
        <button type="button" onClick={refresh} disabled={loading} className="btn-secondary btn-sm h-9 self-start sm:self-auto">
          <RefreshCw size={14} className={clsx(loading && 'animate-spin')} aria-hidden="true" /> Refresh
        </button>
      </div>

      <WebsiteChatSection reloadKey={reloadKey} className="max-w-3xl" />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <DataTable
          label="Web-chat leads"
          columns={columns}
          rows={leads}
          rowKey="id"
          loading={loading}
          error={error}
          onRetry={load}
          stickyHeader={false}
          defaultSort={{ key: 'created_at', dir: 'desc' }}
          onRowClick={(l) => setSelected(l)}
          empty={{
            icon: Inbox,
            title: 'No web-chat leads yet',
            hint: 'When a visitor chats with the widget on your website, their enquiry appears here. Turn on website chat above to start.',
          }}
        />
      </div>

      {selected && <WebChatLeadDialog lead={selected} onClose={closeLead} />}
    </div>
  );
}
